/**
 * Entity Picker Page — Admin landing page.
 * Select an entity to view its students.
 */

import { Link } from 'react-router-dom';
import { useEntities } from '@/lib/hooks/useEntities';
import { Building2, Users, ChevronRight } from 'lucide-react';

export function EntityPickerPage() {
  const { entities, loading, error } = useEntities();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading entities</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64" role="alert">
        <p className="text-destructive text-sm font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <nav aria-label="Breadcrumb" className="flex items-center text-[11px] font-bold tracking-widest text-muted-foreground uppercase mb-6 animate-fade-up">
        <span className="text-primary" aria-current="page">Entities</span>
      </nav>

      <div className="mb-8 animate-fade-up">
        <h2 className="text-3xl font-extrabold tracking-tight text-foreground">Select Entity</h2>
        <p className="text-muted-foreground mt-1">Choose an institution to view its student records.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-up-delay-1">
        {entities.map((entity) => (
          <Link
            key={entity.id}
            to={`/entities/${entity.id}/students`}
            className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all group block"
            aria-label={`${entity.name} — ${entity.studentCount} students`}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center text-primary" aria-hidden="true">
                <Building2 className="w-5 h-5" />
              </div>
              <ChevronRight className="w-5 h-5 text-surface-highest group-hover:text-primary transition-colors" aria-hidden="true" />
            </div>
            <h3 className="text-base font-bold text-foreground mb-1">{entity.name}</h3>
            <div className="flex items-center gap-2 mt-3">
              <Users className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm text-muted-foreground">{entity.studentCount} students</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
